import React from 'react';
import {
  Avatar,
  Button,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Select,
  Skeleton,
  Tag,
  Tooltip,
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye,
  faMagnifyingGlass,
  faPen,
  faSync,
  faTrash,
  faUser,
} from '@fortawesome/pro-solid-svg-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  deleteUser,
  getUsers,
  syncWorkspaceUsers,
  updateUser,
  UserWithStats,
} from '../../services/users';
import { useAuthStore } from '../../store/authStore';
import { colors } from '../../theme/colors';
import type { UserRole } from '../../types';
import PlusAccessPanel from './PlusAccessPanel';
import './Admin.css';

const ROLE_OPTIONS: Array<{ value: UserRole; label: string }> = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'DEVELOPER', label: 'Developer' },
  { value: 'DESIGNER', label: 'Designer' },
  { value: 'MEMBER', label: 'Member' },
  { value: 'VIEWER', label: 'Viewer' },
];

const ROLE_LABELS: Record<UserRole, string> = ROLE_OPTIONS.reduce((labels, option) => ({
  ...labels,
  [option.value]: option.label,
}), {} as Record<UserRole, string>);

type RoleFilter = UserRole | 'ALL';

const Admin: React.FC = () => {
  const { user: currentUser } = useAuthStore();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [selectedUser, setSelectedUser] = React.useState<UserWithStats | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [roleFilter, setRoleFilter] = React.useState<RoleFilter>('ALL');
  const [departmentFilter, setDepartmentFilter] = React.useState('ALL');

  const {
    data: users = [],
    isLoading,
    isError,
    refetch,
  } = useQuery<UserWithStats[]>({
    queryKey: ['users'],
    queryFn: getUsers,
  });


  const updateUserMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: any }) => updateUser(userId, data),
    onSuccess: () => {
      message.success('Member updated');
      setIsEditModalOpen(false);
      setSelectedUser(null);
      form.resetFields();
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to update member');
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      message.success('Member removed from workspace');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to delete member');
    },
  });

  const syncUsersMutation = useMutation({
    mutationFn: syncWorkspaceUsers,
    onSuccess: (data) => {
      const skippedLabel = data.skipped > 0 ? `, ${data.skipped} skipped` : '';
      const removedLabel = data.removed > 0 ? `, ${data.removed} removed` : '';
      const errorsLabel = data.errors.length > 0 ? `, ${data.errors.length} failed` : '';
      const content = `Synced ${data.total} workspace users: ${data.created} created, ${data.updated} updated${removedLabel}${skippedLabel}${errorsLabel}`;
      if (data.errors.length > 0) message.warning(content);
      else message.success(content);
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: any) => {
      const timeoutMessage = error.code === 'ECONNABORTED'
        ? 'Workspace sync timed out. The backend may still be processing; refresh and try again in a minute.'
        : 'Failed to sync workspace users';
      message.error(error.response?.data?.error || timeoutMessage);
    },
  });

  const roleCounts = React.useMemo(() => ROLE_OPTIONS.reduce((counts, role) => ({
    ...counts,
    [role.value]: users.filter((user: UserWithStats) => user.role === role.value).length,
  }), {} as Record<UserRole, number>), [users]);

  const departments = React.useMemo(() => Array.from(new Set<string>(
    users.map((user: UserWithStats) => user.department?.trim()).filter((department: string | undefined): department is string => Boolean(department))
  )).sort((a, b) => a.localeCompare(b)), [users]);

  const filteredUsers = React.useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();
    return users.filter((user: UserWithStats) => {
      const matchesSearch = !normalizedSearch || [
        user.name,
        user.email,
        user.department,
        user.manager?.name,
      ].some((value) => value?.toLowerCase().includes(normalizedSearch));
      const matchesRole = roleFilter === 'ALL' || user.role === roleFilter;
      const matchesDepartment = departmentFilter === 'ALL' || user.department === departmentFilter;
      return matchesSearch && matchesRole && matchesDepartment;
    });
  }, [departmentFilter, roleFilter, searchQuery, users]);

  const hasFilters = Boolean(searchQuery || roleFilter !== 'ALL' || departmentFilter !== 'ALL');

  const clearFilters = () => {
    setSearchQuery('');
    setRoleFilter('ALL');
    setDepartmentFilter('ALL');
  };

  const handleEdit = (user: UserWithStats) => {
    setSelectedUser(user);
    form.setFieldsValue({
      name: user.name,
      email: user.email,
      role: user.role,
      department: user.department || '',
      managerId: user.managerId || undefined,
    });
    setIsEditModalOpen(true);
  };

  const handleView = (user: UserWithStats) => {
    setSelectedUser(user);
    setIsViewModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedUser(null);
    form.resetFields();
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedUser(null);
  };

  const handleUpdate = async () => {
    if (!selectedUser) return;
    try {
      const values = await form.validateFields();
      await updateUserMutation.mutateAsync({ userId: selectedUser.id, data: values });
    } catch {
      // Form errors are displayed inline.
    }
  };

  const renderMember = (member: UserWithStats) => {
    const isCurrentUser = member.id === currentUser?.id;
    const createdWork = (member._count?.createdTasks || 0) + (member._count?.createdIssues || 0);

    return (
      <div className="admin-member-row" key={member.id}>
        <div className="admin-member-identity">
          <Avatar size={36} src={member.photoUrl} icon={<FontAwesomeIcon icon={faUser} />} />
          <div className="admin-member-name-block">
            <div className="admin-member-name-line">
              <span>{member.name}</span>
              {isCurrentUser && <span className="admin-you-label">You</span>}
            </div>
            <span className="admin-member-email">{member.email}</span>
          </div>
        </div>

        <div><Tag bordered={false} className="admin-role-chip">{ROLE_LABELS[member.role]}</Tag></div>
        <div className="admin-member-secondary">{member.department || 'Not set'}</div>
        <div className="admin-member-secondary">{member.manager?.name || 'No manager'}</div>

        <div className="admin-member-activity" aria-label={`${createdWork} created work items`}>
          <span><strong>{member._count?.createdTasks || 0}</strong> tasks</span>
          <span><strong>{member._count?.createdIssues || 0}</strong> issues</span>
          <span><strong>{member._count?.ownedProjects || 0}</strong> projects</span>
        </div>

        <div className="admin-member-last-active">
          {member.lastActiveAt ? dayjs(member.lastActiveAt).format('MMM D, YYYY') : 'Never'}
        </div>

        <div className="admin-member-actions">
          <Tooltip title="View details">
            <Button
              type="text"
              aria-label={`View ${member.name}`}
              icon={<FontAwesomeIcon icon={faEye} />}
              onClick={() => handleView(member)}
            />
          </Tooltip>
          <Tooltip title={isCurrentUser ? 'Edit your profile from Profile settings' : 'Edit member'}>
            <Button
              type="text"
              aria-label={`Edit ${member.name}`}
              icon={<FontAwesomeIcon icon={faPen} />}
              onClick={() => handleEdit(member)}
              disabled={isCurrentUser}
            />
          </Tooltip>
          <Popconfirm
            title="Remove member from workspace?"
            description="Their access and assignments will be removed; historical activity will be retained."
            onConfirm={() => deleteUserMutation.mutate(member.id)}
            okText="Remove"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            disabled={isCurrentUser}
          >
            <Tooltip title={isCurrentUser ? 'You cannot remove your own account' : 'Remove member'}>
              <Button
                type="text"
                danger
                aria-label={`Remove ${member.name}`}
                icon={<FontAwesomeIcon icon={faTrash} />}
                disabled={isCurrentUser}
              />
            </Tooltip>
          </Popconfirm>
        </div>
      </div>
    );
  };

  return (
    <main className="admin-page" style={{ background: colors.bgBase }}>
      <header className="admin-header">
        <div>
          <h1>Member management</h1>
          <p>Manage workspace access, roles, departments, and reporting lines.</p>
        </div>
        <Button
          icon={<FontAwesomeIcon icon={faSync} spin={syncUsersMutation.isPending} />}
          onClick={() => syncUsersMutation.mutate()}
          loading={syncUsersMutation.isPending}
        >
          Sync workspace
        </Button>
      </header>

      <section className="admin-summary" aria-label="Member role summary">
        <div className="admin-summary-item admin-summary-item--total">
          <span>Total members</span>
          <strong>{users.length}</strong>
        </div>
        {ROLE_OPTIONS.map((role) => (
          <div className="admin-summary-item" key={role.value}>
            <span>{role.label}s</span>
            <strong>{roleCounts[role.value] || 0}</strong>
          </div>
        ))}
      </section>

      <section className="admin-directory" aria-label="Workspace members">
        <div className="admin-directory-toolbar">
          <div className="admin-directory-heading">
            <h2>Workspace members</h2>
            <span>{filteredUsers.length}{hasFilters ? ` of ${users.length}` : ''}</span>
          </div>
          <div className="admin-directory-filters">
            <Input
              allowClear
              aria-label="Search members"
              placeholder="Search name, email, or team…"
              prefix={<FontAwesomeIcon icon={faMagnifyingGlass} />}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
            <Select<RoleFilter>
              aria-label="Filter by role"
              value={roleFilter}
              onChange={setRoleFilter}
              options={[
                { value: 'ALL', label: 'All roles' },
                ...ROLE_OPTIONS,
              ]}
            />
            <Select
              aria-label="Filter by department"
              value={departmentFilter}
              onChange={setDepartmentFilter}
              options={[
                { value: 'ALL', label: 'All departments' },
                ...departments.map((department) => ({ value: department, label: department })),
              ]}
            />
            {hasFilters && <Button type="text" onClick={clearFilters}>Clear</Button>}
          </div>
        </div>

        {isLoading ? (
          <div className="admin-directory-loading">
            {[0, 1, 2].map((item) => <Skeleton key={item} active avatar paragraph={{ rows: 1 }} />)}
          </div>
        ) : isError ? (
          <div className="admin-directory-state">
            <strong>Members could not be loaded</strong>
            <span>Check the local backend and try again.</span>
            <Button onClick={() => refetch()}>Try again</Button>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="admin-directory-state">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<span>No members match these filters.</span>}
            />
            <Button onClick={clearFilters}>Clear filters</Button>
          </div>
        ) : (
          <>
            <div className="admin-member-header" aria-hidden="true">
              <span>Member</span>
              <span>Role</span>
              <span>Department</span>
              <span>Manager</span>
              <span>Activity</span>
              <span>Last active</span>
              <span>Actions</span>
            </div>
            <div className="admin-member-list">{filteredUsers.map(renderMember)}</div>
          </>
        )}
      </section>

      <PlusAccessPanel />

      <Modal
        title="Edit member"
        open={isEditModalOpen}
        onOk={handleUpdate}
        onCancel={closeEditModal}
        okText="Update member"
        cancelText="Cancel"
        confirmLoading={updateUserMutation.isPending}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please enter a name' }]}>
            <Input prefix={<FontAwesomeIcon icon={faUser} />} />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter an email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input disabled />
          </Form.Item>
          <Form.Item name="role" label="Role" rules={[{ required: true, message: 'Please select a role' }]}>
            <Select options={ROLE_OPTIONS} />
          </Form.Item>
          <Form.Item name="department" label="Department">
            <Input placeholder="e.g. Engineering" />
          </Form.Item>
          <Form.Item name="managerId" label="Manager">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="No manager"
              options={users
                .filter((user) => user.id !== selectedUser?.id)
                .map((user) => ({ value: user.id, label: user.name }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Member details"
        open={isViewModalOpen}
        onCancel={closeViewModal}
        footer={<Button onClick={closeViewModal}>Close</Button>}
        width={680}
      >
        {selectedUser && (
          <div className="admin-member-detail">
            <div className="admin-member-detail-identity">
              <Avatar size={64} src={selectedUser.photoUrl} icon={<FontAwesomeIcon icon={faUser} />} />
              <div>
                <h3>{selectedUser.name}</h3>
                <span>{selectedUser.email}</span>
              </div>
              <Tag bordered={false} className="admin-role-chip">{ROLE_LABELS[selectedUser.role]}</Tag>
            </div>

            <dl className="admin-member-detail-fields">
              <div><dt>Department</dt><dd>{selectedUser.department || 'Not set'}</dd></div>
              <div><dt>Manager</dt><dd>{selectedUser.manager?.name || 'No manager'}</dd></div>
              <div><dt>Created</dt><dd>{dayjs(selectedUser.createdAt).format('MMM D, YYYY')}</dd></div>
              <div><dt>Last active</dt><dd>{selectedUser.lastActiveAt ? dayjs(selectedUser.lastActiveAt).format('MMM D, YYYY, h:mm A') : 'Never'}</dd></div>
            </dl>

            <div className="admin-member-detail-stats">
              <div><strong>{selectedUser._count?.createdTasks || 0}</strong><span>Tasks created</span></div>
              <div><strong>{selectedUser._count?.assignedTasks || 0}</strong><span>Tasks assigned</span></div>
              <div><strong>{selectedUser._count?.createdIssues || 0}</strong><span>Issues created</span></div>
              <div><strong>{selectedUser._count?.assignedIssues || 0}</strong><span>Issues assigned</span></div>
              <div><strong>{selectedUser._count?.ownedProjects || 0}</strong><span>Projects owned</span></div>
            </div>
          </div>
        )}
      </Modal>
    </main>
  );
};

export default Admin;
